import {
  BorderRadiusProps,
  DisplayProps,
  FontSizeProps,
  FontWeightProps,
  LineHeightProps,
  MinWidthProps,
  SpaceProps,
  WidthProps,
  borderRadius,
  display,
  fontSize,
  fontWeight,
  lineHeight,
  minWidth,
  space,
  width,
  compose,
  BackgroundProps,
} from 'styled-system';
import Label from 'components/_shared/elements/label';
import { useId, useState, InputHTMLAttributes } from 'react';
import { css } from '@emotion/react';
import styled from 'components/styled';
import { Box } from 'components/box';
import FormError from 'components/form/error';
import { Text } from 'components/typography';

interface TextAreaComponentProps {
  invalid?: boolean;
  height?: number;
  label?: string;
}

type Props = TextAreaComponentProps &
  SpaceProps &
  FontSizeProps &
  WidthProps &
  FontWeightProps &
  LineHeightProps &
  BorderRadiusProps &
  BackgroundProps &
  MinWidthProps &
  DisplayProps;

const TextAreaComponent = styled.textarea<Props>`
  ${({ invalid = false, height = 8, theme }) => {
    return css`
      outline: none;
      resize: none;
      appearance: none;
      display: block;
      width: 100%;
      height: ${height}em;
      font-family: ${theme.fonts.body};
      font-size: ${theme.fontSizes[2]};
      border-width: 2px;
      border-style: solid;
      border-color: ${theme.colors.borderColor};
      padding-top: 14px;
      padding-bottom: 14px;
      padding-left: 12px;
      padding-right: 12px;
      margin: 0;

      ${invalid
        ? css`
            border-color: ${theme.colors.pink};
          `
        : ''}

      &:active,
     &:focus {
        border-color: ${theme.colors.primary};
      }

      ::placeholder {
        color: ${theme.colors.darkGrey};
      }
      ::-ms-clear {
        color: ${theme.colors.primary};
      }
    `;
  }}
  ${compose(
    space,
    fontSize,
    width,
    fontWeight,
    lineHeight,
    borderRadius,
    minWidth,
    display
  )}
`;

TextAreaComponent.defaultProps = {
  borderRadius: 1,
};

type TextAreaProps = {
  label?: string;
  subLabel?: string;
} & InputHTMLAttributes<HTMLTextAreaElement> &
  Props;

const TextArea = ({ label, subLabel, invalid, id, ...rest }: TextAreaProps) => {
  const [isFocused, setFocus] = useState(false);
  const idRef = useId();
  const htmlId = id || `${rest.name}-${idRef}`;

  return (
    <>
      {label && (
        <Label mb={2} htmlFor={htmlId} invalid={invalid} isFocused={isFocused}>
          {label}
        </Label>
      )}
      {subLabel && (
        <Text mb={2} fontSize="0.95rem" fontWeight="regular">
          {subLabel}
        </Text>
      )}
      <TextAreaComponent
        {...rest}
        id={htmlId}
        invalid={invalid}
        onFocus={e => {
          setFocus(true);
          rest.onFocus && rest.onFocus(e);
        }}
        onBlur={e => {
          setFocus(false);
          rest.onBlur && rest.onBlur(e);
        }}
      />
    </>
  );
};

const TextAreaWrapper = ({
  error,
  invalid,
  ...rest
}: TextAreaProps & { error?: string; invalid?: boolean }) => (
  <Box>
    <TextArea {...rest} />
    {error && invalid && <FormError>{error}</FormError>}
  </Box>
);

export default TextAreaWrapper;
